<template>
  <div class="container">
    <h1 class="subs-title">{{ organizationText(getTitle()) }}</h1>
    <text-field v-model="form.name" :validator="$v.form.name" :label="adminText('organizationAll.subscription_name_e7d80671', 'Subscription name*')" />
    <select-field
      v-model="form.season"
      :validator="$v.form.season"
      :label="adminText('organizationAll.season_2a55a0c3', 'Season*')"
      :items="allSeasons"
    />
    <div class="input-container">
      <datetime-field
        v-model="form.from"
        :validator="$v.form.from"
        style="width: 100%"
        :label="adminText('organizationAll.from_4a2da276', 'From*')"
        :min="new Date().toString()"
        :disabled="!isEmpty(initialValue) && !!initialValue.prepaidCouponUser.length"
        :error-messages="{
          minDate: adminText('organizationAll.start_date_must_be_a_future_date_49b63d67', 'Start date must be a future date'),
          maxDate: adminText('organizationAll.start_date_should_be_before_the_end_of_the_ev_d4de23c0', 'Start date should be before the end of the event'),
        }"
      />
      <datetime-field
        v-model="form.to"
        :validator="$v.form.to"
        style="width: 100%"
        :label="adminText('organizationAll.to_c2fdddd0', 'To*')"
        :min="form.from ? form.from.toString() : new Date().toString()"
        :disabled="!isEmpty(initialValue) && !!initialValue.prepaidCouponUser.length"
        :error-messages="{
          minDate: adminText('organizationAll.end_date_should_not_be_before_the_start_of_th_0753b659', 'End date should not be before the start of the event'),
        }"
      />
    </div>
    <div class="input-container">
      <text-field
        v-model.number="form.price"
        :validator="$v.form.price"
        type="number"
        step="0.01"
        :label="adminText('organizationAll.price_eur_444903ee', 'Price (EUR)*')"
      />
    </div>
    <div class="input-container">
      <v-checkbox
        v-model="form.itemMeta.sportMembershipTicket"
        :label="adminText('organizationAll.subscription_valid_only_for_club_members_85be3b22', 'Subscription valid only for club members.')"
        style="margin: 0 !important; padding: 0 !important"
      />
    </div>
    <cover-field
      v-if="!organization.imageUrls.subscriptionCoverUrl"
      v-model="form.imageUrl"
      :validator="$v.form.imageUrl"
    />
    <textarea-field
      v-model="form.description"
      :validator="$v.form.description"
      :label="adminText('organizationAll.description_55f8ebc8', 'Description')"
      height="13.75rem"
    />
    <h2 class="second-title">{{ adminText('organizationAll.exclude_events_c86e53d3', 'Exclude events') }}</h2>
    <p class="description">{{ adminText('organizationAll.you_can_exclude_tickets_for_performances_by_e_8bbab981', 'You can exclude tickets for performances by events name.') }}</p>
    <select-field-multiple
      v-model="form.excludedEventIds"
      :label="adminText('organizationAll.exclude_events_c86e53d3', 'Exclude events')"
      :items="allEventsInRange"
      item-render="title"
      item-select="id"
      :disabled="!isEmpty(initialValue) && !!initialValue.prepaidCouponUser.length"
    />

    <div class="buttons-wrapper" style="margin-top: 58px">
      <main-button :text="adminText('organizationAll.cancel_1507c40f', 'CANCEL')" flat depressed @click="$emit('cancel')" />
      <div class="buttons">
        <main-button :text="organizationText(buttonName)" @click="handleCreate" />
      </div>
    </div>
  </div>
</template>

<script>
import OrganizationLocalization from '@/components/organizations/organization-all-display'

import { isEmpty, omit } from 'lodash'
import { required, requiredIf } from 'vuelidate/lib/validators'
import { submitGuard } from '@/utils'
import moment from 'moment'

export default {
  mixins: [OrganizationLocalization],
  name: 'create-or-edit-sport-subscription-plan',
  props: {
    organizationSlug: {
      type: String,
    },
    initialValue: {
      type: Object,
      default: () => {},
    },
    eventTemplates: {
      type: Array,
      default: () => [],
    },
    interests: {
      type: Array,
      default: () => [],
    },
    allEvents: {
      type: Array,
      default: () => [],
    },
    organization: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    const eventIds =
      this.initialValue.couponItems && this.initialValue.couponItems.length
        ? this.initialValue.couponItems[0].eventIds
        : []
    const excludedEventIds = !isEmpty(this.initialValue)
      ? this.filterEventsByRange(this.allEvents, this.initialValue.from, this.initialValue.to)
        .map(e => e.id)
        .filter(id => !eventIds.includes(id))
      : []

    return {
      form: {
        itemMeta: {
          sportMembershipTicket: false,
        },
        ...omit(this.initialValue, ['couponItems', 'currency']),
        excludedEventIds,
      },
      buttonName: 'Create plan',
      emitType: 'create',
      attendeeFields: {},
    }
  },
  computed: {
    allEventsInRange() {
      if (!this.form.from || !this.form.to) {
        return this.allEvents
      }

      return this.filterEventsByRange(this.allEvents, this.form.from, this.form.to)
    },
    allSeasons() {
      const currentYear = new Date().getFullYear()
      let middleSeason

      // If there are initial values, use the selected season as middle
      if (!isEmpty(this.initialValue) && this.initialValue.season) {
        middleSeason = this.initialValue.season
      } else {
        // Otherwise use current year/next-year as middle
        middleSeason = `${currentYear}./${currentYear + 1}.`
      }

      // Extract the starting year from the middle season (e.g., "2023/2024" -> 2023)
      const middleYear = parseInt(middleSeason.split('/')[0])

      return [
        `${middleYear - 2}./${middleYear - 1}.`,
        `${middleYear - 1}./${middleYear}.`,
        middleSeason, // Selected season or current year/next-year as 3rd element
        `${middleYear + 1}./${middleYear + 2}.`,
        `${middleYear + 2}./${middleYear + 3}.`,
      ]
    },
  },
  validations() {
    return {
      form: {
        name: { required },
        season: { required },
        price: { required },
        from: { required },
        to: { required },
        imageUrl: { required: requiredIf(() => !this.organization.imageUrls.subscriptionCoverUrl) },
      },
    }
  },
  methods: {
    isEmpty,
    getTitle() {
      if (Object.keys(this.initialValue).length) {
        this.buttonName = 'Update'
        this.emitType = 'update'

        return 'Edit Subscription Plan'
      } else {
        return 'Create Subscription Plan'
      }
    },
    removeSubscriptionItem(externalId) {
      this.form.couponItems.splice(
        this.form.couponItems.findIndex(item => item.externalId === externalId),
        1,
      )
    },
    updateCouponItem(info) {
      this.form.couponItems = this.form.couponItems.map(ci =>
        ci.externalId === info.externalId ? info : ci,
      )
    },
    handleCreate() {
      submitGuard(this, () => this.$emit(this.emitType, this.form))
    },
    filterEventsByRange(events, from, to) {
      return events.filter(
        event => moment(from).isBefore(moment(event.from)) && moment(to).isAfter(moment(event.to)),
      )
    },
  },
}
</script>

<style scoped>
.container {
  padding: 0 !important;
  margin: 0 !important;
  display: flex;
  flex-direction: column;
  max-width: 43.75rem;
}
.subs-title {
  font-family: 'Raleway';
  font-style: normal;
  font-weight: 600;
  font-size: 24px;
  line-height: 120%;
  letter-spacing: -0.03em;
  font-feature-settings: 'pnum' on, 'lnum' on;
  color: #ff4c00;
  margin-bottom: 1.5rem;
}
.input-container {
  display: flex;
  justify-content: space-between;
  width: 100%;
  column-gap: 1.5rem;
}
.second-title {
  font-family: 'Raleway';
  font-style: normal;
  font-weight: 600;
  font-size: 20px;
  color: #ff4c00;
  margin-bottom: 0.75rem;
  margin-top: 3rem;
}
</style>
